import * as classNames from 'classnames';
import { LeadGroupModalStatus } from 'common/interfaces/provider';
import { UserType } from 'common/interfaces/user';
import { IIconButtonProps } from 'components/action';
import { FullScreenMessage, SectionHeadline } from 'components/content';
import { LoadingFullScreen } from 'components/indicator';
import { Layout } from 'components/layout';
import { scrollWindow } from 'events/scroll';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IApp } from 'stores/AllStores';
import * as zenscroll from 'zenscroll';
import { BusinessGroupModal } from './BusinessGroup';
import { BusinessPartnerSlider } from './BusinessPartner';
import { LeadDisable } from './Disable';
import { PricingRequestModal } from './PricingRequest';
import { LeadQuestions } from './Questions/LeadQuestions';
import { RepCollectionsModal } from './Sales/RepCollectionsModal';
import {
  LeadDetailsMaster,
  LeadContacts,
  LeadActivities,
  OtherActivities,
  LeadNotes,
  LeadResources,
  PricingRequestSidebar,
  RepSales,
} from '.';
import { LeadRelationsModal } from './Relations/LeadRelationsModal';
import { LeadRelations } from './Relations';

// Styles
const s = require('./SalesLead.css');
const editIcon = require('assets/pen icon.svg');
const addIcon = require('assets/plus icon.svg');
const cancelIcon = require('assets/close orange icon.svg');
const arrowIcon = require('assets/blue arrow.svg');
const visibilityIcon = require('assets/visibility button.svg');
const padlockIcon = require('assets/padlock-icon.svg');

// Page
@inject('store')
@observer
export class SalesLead extends React.Component<IApp, {}> {
  constructor(props: IApp) {
    super(props);

    // Load details of lead from current page
    this.loadLead();

    // Fetch provider types and dispenser classes
    this.props.store.leads.fetchProviderTypes();
    this.props.store.leads.fetchDispenserClasses();
  }

  // Get ID of the lead param
  @computed private get id(): number | null {
    const parsedId = parseInt(this.props.match.params.providerId, 10);
    return isNaN(parsedId) ? null : parsedId;
  }

  @observable private isLoading: boolean = true;
  @observable private noMatch: boolean = false;

  @computed private get hasContacts(): boolean {
    return this.props.store.leadContacts.combined.length > 0;
  }

  @computed private get contactsEditButton(): IIconButtonProps {
    const { leadContacts } = this.props.store;

    if (leadContacts.editing) {
      return {
        onClick: leadContacts.cancelEditingContacts,
        label: 'Cancel editing',
        icon: cancelIcon,
      };
    } else if (this.hasContacts) {
      return {
        onClick: this.handleContactsEditing,
        label: 'Edit',
        icon: editIcon,
      };
    } else {
      return null;
    }
  }

  @computed private get hasResources(): boolean {
    return this.props.store.leadResources.all.length > 0;
  }

  @computed private get resourcesEditButton(): IIconButtonProps {
    const { leadResources } = this.props.store;

    if (this.hasResources && leadResources.editing) {
      return {
        onClick: leadResources.stopEditing,
        label: 'Cancel editing',
        icon: cancelIcon,
      };
    } else if (this.hasResources) {
      return {
        onClick: leadResources.startEditing,
        label: 'Edit',
        icon: editIcon,
      };
    } else {
      return null;
    }
  }

  @computed private get isManager(): boolean {
    const { userType } = this.props.store.user;
    return userType === UserType.SalesManager;
  }

  public componentDidMount() {
    zenscroll.toY(0);
  }

  public componentDidUpdate(prevProps: IApp) {
    let prevProviderId: number = 0;
    let newProviderId: number = 0;

    if (typeof prevProps.match.params.providerId !== 'undefined') {
      prevProviderId = parseInt(prevProps.match.params.providerId, 10);
    }

    if (typeof this.props.match.params.providerId !== 'undefined') {
      newProviderId = parseInt(this.props.match.params.providerId, 10);
    }

    if (prevProviderId !== newProviderId) {
      this.loadLead();
    }
  }

  public componentWillUnmount() {
    this.props.store.lead.handleOpenLeadDisable();
  }

  public render() {
    const {
      lead,
      leadGroup,
      leadActivities,
      leadContacts,
      leadPricing,
      pricingRequest,
      businessPartner,
      orderPadSales,
      user,
    } = this.props.store;

    return (
      <Layout>
        {this.noMatch === false && lead.openLeadDetails !== null && (
          <div className={s.lead}>
            <div
              className={classNames(s.details, {
                [s.creating]: leadContacts.creating,
                [s.businessPartnerPanelOpen]:
                  businessPartner.newRequest.showModal,
              })}
            >
              {this.isManager &&
                lead.openLeadDetails !== null &&
                !lead.openLeadDetails.customerCreated &&
                !businessPartner.newRequest.promoted && (
                  <div className={classNames(s.block, s.createAccount)}>
                    <SectionHeadline
                      title={
                        businessPartner.newRequest.showModal
                          ? 'Cancel'
                          : 'Create account in SAP'
                      }
                      button={
                        businessPartner.newRequest.showModal
                          ? {
                              icon: cancelIcon,
                              labelOnRight: false,
                              onClick:
                                businessPartner.hideBusinessPartnerRequestModal,
                            }
                          : {
                              icon: addIcon,
                              labelOnRight: false,
                              onClick: this.handleBusinessPartnerRequestClick,
                            }
                      }
                    />
                    <hr />
                  </div>
                )}

              {businessPartner.newRequest.promoted && (
                <div className={classNames(s.block, s.createAccount)}>
                  <SectionHeadline
                    title={businessPartner.newRequest.promoted}
                  />
                  <hr />
                </div>
              )}

              <div className={classNames(s.block, s.pricing)}>
                <SectionHeadline
                  title={`${lead.leadTitle} pricing`}
                  button={{
                    onClick: this.handleViewLeadPricing,
                    label: this.isManager ? 'View and edit' : 'View',
                    icon: arrowIcon,
                    iconClassName: s.arrow,
                  }}
                />
                <hr />
              </div>

              <div className={classNames(s.block, s.provider)}>
                <SectionHeadline
                  title={`${lead.leadTitle} details`}
                  button={
                    lead.openLeadEditingDetails
                      ? {
                          onClick: lead.stopEditingOpenLeadDetails,
                          label: 'Cancel editing',
                          icon: cancelIcon,
                        }
                      : {
                          onClick: lead.startEditingOpenLeadDetails,
                          label: 'Edit',
                          icon: editIcon,
                        }
                  }
                />
                <LeadDetailsMaster />
                <hr />
              </div>

              {lead.openLeadDetails &&
                lead.openLeadDetails.relations.length > 0 && (
                  <div className={classNames(s.block, s.relations)}>
                    <SectionHeadline
                      title={`${lead.leadTitle} relations`}
                      button={{
                        onClick: this.handleViewLeadRelations,
                        label: 'View relations',
                        icon: arrowIcon,
                        iconClassName: s.arrow,
                      }}
                    />
                    <LeadRelations />
                    <hr />
                  </div>
                )}

              <div
                className={classNames(s.block, s.contacts)}
                ref={this.referenceContactsSection}
              >
                <SectionHeadline
                  title={`${lead.leadTitle} contacts`}
                  button={this.contactsEditButton}
                />
                <LeadContacts />
                <hr />
              </div>

              {businessPartner.newRequest.showModal === false && (
                <div className={classNames(s.block, s.pricingReqest)}>
                  <SectionHeadline
                    title={`Pricing request${
                      leadPricing.pendingPricingRequests.length > 1 ? 's' : ''
                    }`}
                    button={
                      user.userType === UserType.SalesRep
                        ? {
                            icon: addIcon,
                            labelOnRight: false,
                            label: 'Create pricing request',
                            onClick: this.handleCreatePricingRequestClick,
                          }
                        : null
                    }
                  />
                  <PricingRequestSidebar />
                  <hr />
                </div>
              )}

              {businessPartner.newRequest.showModal === false && (
                <div className={classNames(s.block, s.disable)}>
                  <SectionHeadline
                    title={`Disable ${lead.leadTitle}`}
                    button={{
                      icon: padlockIcon,
                      iconClassName: s.disableIcon,
                      labelOnRight: false,
                      label: 'Disable',
                      onClick: this.handleDisableLeadClick,
                    }}
                  />
                  <LeadDisable />
                </div>
              )}
            </div>

            <div className={s.middle}>
              <div className={s.sales}>
                <RepSales />
              </div>

              <div className={s.activities}>
                <LeadActivities />
              </div>

              <LeadNotes />
            </div>

            <div className={s.other}>
              <div className={s.otherLeadsActivities}>
                <SectionHeadline
                  title={`Other ${lead.leadTitle}s' activities`}
                  button={{
                    onClick: this.handleToggleShowingActivitiesOfOtherLeads,
                    label: leadActivities.showActivitiesOfOtherLeads
                      ? 'Hide'
                      : 'Show',
                    icon: visibilityIcon,
                  }}
                />
                {leadActivities.showActivitiesOfOtherLeads && (
                  <OtherActivities />
                )}
              </div>

              <div className={s.questionnaire}>
                <SectionHeadline
                  title="Questionnaire"
                  button={{
                    onClick: this.handleTogglingQuestionnaireVisibility,
                    label: lead.isQuestionnaireVisible ? 'Hide' : 'Show',
                    icon: visibilityIcon,
                  }}
                />

                {lead.isQuestionnaireVisible && <LeadQuestions />}
              </div>

              <div className={s.resources}>
                <SectionHeadline
                  title="Resources"
                  button={this.resourcesEditButton}
                />
                <LeadResources />
              </div>
            </div>
          </div>
        )}

        <LeadRelationsModal show={lead.showRelationsOfOpenLead} />

        <PricingRequestModal
          show={
            leadPricing.newRequest.showModal ||
            pricingRequest.singlePricingRequest !== null
          }
          existingRequest={
            pricingRequest.singlePricingRequest
              ? {
                  data: pricingRequest.singlePricingRequest,
                  status: pricingRequest.statusOfSinglePricingRequestApproval,
                  loading: pricingRequest.loadingSinglePricingRequest,
                }
              : null
          }
          onModalClose={this.handlePricingRequestModalClose}
        />

        <PricingRequestModal
          show={leadPricing.leadItemsPricingModal}
          existingRequest={{
            data: leadPricing.leadItemsPricingModalData,
            status: leadPricing.statusOfUpdatingPricesForAllItems,
            onlyItems: true,
            loading: false,
          }}
          onModalClose={this.handleItemsPricingModalClose}
        />

        <BusinessGroupModal
          show={leadGroup.modalStatus !== LeadGroupModalStatus.Closed}
        />

        <BusinessPartnerSlider show={businessPartner.newRequest.showModal} />

        <RepCollectionsModal show={orderPadSales.showCollectionsModal} />

        {this.isLoading === false && this.noMatch === true && (
          <FullScreenMessage
            label={`No lead of ID ${this.id} exists`}
            dismiss={{
              label: 'Go back',
              onClick: this.handleNavigateBack,
            }}
          />
        )}

        <LoadingFullScreen
          active={this.isLoading === true && this.noMatch === false}
        />
      </Layout>
    );
  }

  private contactsSection: HTMLDivElement = null;
  private referenceContactsSection = (ref: HTMLDivElement) =>
    (this.contactsSection = ref);

  private handleNavigateBack = () => {
    this.props.history.goBack();
  };

  @action
  private handleViewLeadRelations = () => {
    this.props.store.lead.showRelationsOfOpenLead = true;
  };

  private handleContactsEditing = () => {
    if (this.contactsSection) {
      scrollWindow.to(this.contactsSection);
    }

    this.props.store.leadContacts.startEditingContacts();
  };

  private handleViewLeadPricing = () => {
    this.props.store.leadPricing.leadItemsPricingModal = true;
  };

  private handleCreatePricingRequestClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    this.props.store.leadPricing.showCreatePricingRequestModal(true);
  };

  private handleDisableLeadClick = () => {
    this.props.store.lead.openDisableLeadModal();
  };

  private handleToggleShowingActivitiesOfOtherLeads = () => {
    this.props.store.leadActivities.toggleShowingOfActivitiesOfOtherLeads();
  };

  private handleTogglingQuestionnaireVisibility = () => {
    this.props.store.lead.toggleQuestionnaireVisibility();
  };

  @action private loadLead = async () => {
    // Being loading
    this.isLoading = true;
    this.noMatch = false;

    try {
      // Load lead of ID and hide loader
      if (this.id) {
        await this.loadDetails();
        await this.loadContacts();
        await this.loadResources();
      } else {
        throw new Error('Lead page has no lead ID provided in route path');
      }
    } catch (error) {
      // Call error
      if (
        typeof error !== 'undefined' &&
        error &&
        typeof error.code !== 'undefined' &&
        error.code === 404
      ) {
        this.noMatch = true;
        console.error('Lead does not exist');
      } else {
        setTimeout(() => {
          this.loadLead();
        }, 1024);
      }
    } finally {
      this.isLoading = false;
    }
  };

  private loadDetails = async () => {
    await this.props.store.lead.handleLeadLoad(this.id);
  };

  private loadContacts = async () => {
    await this.props.store.leadContacts.fetchLeadContacts(this.id);
  };

  private loadResources = async () => {
    await this.props.store.leadResources.fetchLeadResources(this.id);
  };

  private handlePricingRequestModalClose = () => {
    this.props.store.leadPricing.clearAll();
    this.props.store.leadPricing.fetchAllItemsWithPricing();
    this.props.store.pricingRequest.resetSinglePricingRequest();
  };

  private handleItemsPricingModalClose = () => {
    this.props.store.leadPricing.leadItemsPricingModal = false;
  };

  private handleBusinessPartnerRequestClick = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    this.props.store.businessPartner.showBusinessPartnerRequestModal();
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/SalesLead.tsx